import type {CssCustomProperties} from '@wix/component-protocol/schema'
import {SKIP_STYLE_LIST, STYLE_TYPE_MAP} from '../constants/style'
import type {SerializedVariable} from '../types'
import {convertDMVariableToCSSValue} from '../dataMappers'

type CssCustomPropertyItem = CssCustomProperties[string]

export interface ProcessedStyleVariable {
    name: string
    styleItem: CssCustomPropertyItem
}

export interface IVariableStyleTransformer {
    transformVariablesToStyles(
        variables: SerializedVariable[],
        defaultValues: Record<string, unknown>
    ): CssCustomProperties
}

export class VariableStyleTransformer implements IVariableStyleTransformer {
    transformVariablesToStyles(
        variables: SerializedVariable[],
        defaultValues: Record<string, unknown>
    ): CssCustomProperties {
        const processedVariables = variables
            .filter(variable => variable.name && defaultValues[variable.name])
            .map(variable => this.transformVariableToStyle(variable, defaultValues[variable.name]))
            .filter((item): item is ProcessedStyleVariable => item !== null)

        return Object.fromEntries(processedVariables.map(({name, styleItem}) => [name, styleItem]))
    }

    private transformVariableToStyle(
        variable: SerializedVariable,
        defaultValue: unknown
    ): ProcessedStyleVariable | null {
        if (this.shouldSkipVariable(variable, defaultValue)) {
            return null
        }

        const cssPropertyType = STYLE_TYPE_MAP[variable.type]
        const cssValue = convertDMVariableToCSSValue(cssPropertyType, defaultValue)

        return {
            name: variable.name,
            styleItem: {
                cssPropertyType,
                displayName: variable.name,
                defaultValue: cssValue
            }
        }
    }

    private shouldSkipVariable(variable: SerializedVariable, defaultValue: unknown): boolean {
        if (!variable.name || !defaultValue) return true

        if (SKIP_STYLE_LIST.includes(variable.type)) return true

        return !STYLE_TYPE_MAP[variable.type]
    }
}
